import { type DocumentValueInput, type DocumentValueType } from "~/graphql/supergraphTypes";
import { FATCA_PARTS } from "../configs/w8bene.config";
import {
  type AddressValues,
  type TaxFormFieldValue,
  type TaxFormType,
  type TaxFormValues,
  type TaxResidencyEntry,
} from "../types";

type BackendAddressData = {
  country?: string;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
};

const TAX_FORM_TYPE_MAP: Record<TaxFormType, string> = {
  W9: "W-9",
  W8BEN: "W-8BEN",
  W8BENE: "W-8BEN-E",
  W8ECI: "W-8ECI",
  W8EXP: "W-8EXP",
  W8IMY: "W-8IMY",
  WITHHOLDING_STATEMENT: "withholding_statement",
  CRS_INDIVIDUAL: "individual_self_certification",
  CRS_ENTITY: "entity_self_certification",
};

const LOB_TYPE_OTHER = "other";

const isAddressValues = (value: TaxFormFieldValue): value is AddressValues =>
  typeof value === "object" && value !== null && "country" in value && "streetLine1" in value;

const transformAddressToBackend = (address: AddressValues): BackendAddressData => ({
  country: address.country || undefined,
  street: address.streetLine1 || undefined,
  street2: address.streetLine2 || undefined,
  city: address.city || undefined,
  state: address.stateOrProvince || undefined,
  postal_code: address.zipCode || undefined,
});

const addMetadataEntry = (
  metadata: DocumentValueInput[],
  key: string,
  value: unknown,
  type: "string" | "boolean" | "date" = "string",
): void => {
  if (value === undefined || value === null || value === "") return;

  // Skip objects (addresses are handled by addAddressMetadata)
  if (typeof value === "object" && !(value instanceof Date)) return;

  let stringValue: string;
  if (type === "boolean" && typeof value === "boolean") {
    stringValue = String(value);
  } else if (type === "date" && value instanceof Date) {
    stringValue = value.toISOString();
  } else if (typeof value === "string" || typeof value === "number") {
    stringValue = String(value);
  } else {
    return; // Skip unsupported types
  }

  metadata.push({ key, value: stringValue, type });
};

const addAddressMetadata = (
  metadata: DocumentValueInput[],
  key: string,
  value: TaxFormFieldValue,
): void => {
  if (!isAddressValues(value)) return;
  const address = transformAddressToBackend(value);
  const addressObject = Object.fromEntries(
    Object.entries(address).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );
  if (Object.keys(addressObject).length > 0) {
    metadata.push({ key, value: JSON.stringify(addressObject), type: "string" });
  }
};

type BackendTaxResidencyData = {
  jurisdiction: string;
  has_tax_reference_number: boolean;
  tax_reference_number_type?: string;
  tax_reference_number?: string;
  reason_for_no_tin?: string;
};

const isTaxResidencyArray = (value: TaxFormFieldValue): value is TaxResidencyEntry[] =>
  Array.isArray(value) &&
  value.every((item) => typeof item === "object" && "jurisdiction" in item);

const transformTaxResidencyToBackend = (entry: TaxResidencyEntry): BackendTaxResidencyData => ({
  jurisdiction: entry.jurisdiction,
  has_tax_reference_number: entry.hasTaxReferenceNumber,
  tax_reference_number_type: entry.taxReferenceNumberType || undefined,
  tax_reference_number: entry.taxReferenceNumber || undefined,
  reason_for_no_tin: entry.reasonForNoTin || undefined,
});

const addTaxResidenciesMetadata = (
  metadata: DocumentValueInput[],
  key: string,
  value: TaxFormFieldValue,
): void => {
  if (!isTaxResidencyArray(value) || value.length === 0) return;
  const confirmedEntries = value.filter((e) => !e._isDraft);
  if (confirmedEntries.length === 0) return;
  const transformedEntries = confirmedEntries.map(transformTaxResidencyToBackend);
  metadata.push({ key, value: JSON.stringify(transformedEntries), type: "string" });
};

const transformW9 = (values: TaxFormValues): DocumentValueInput[] => {
  const metadata: DocumentValueInput[] = [];
  addMetadataEntry(metadata, "type", TAX_FORM_TYPE_MAP.W9);
  addMetadataEntry(metadata, "name", values.name);
  addMetadataEntry(metadata, "disregarded_entity_name", values.disregardedEntityName);
  addMetadataEntry(metadata, "tax_classification", values.taxClassification);
  addMetadataEntry(metadata, "llc_classification", values.llcTaxClassification);
  addMetadataEntry(metadata, "exempt_payee_code", values.exemptPayeeCode);
  addMetadataEntry(metadata, "exemption_from_fatca_reporting_code", values.fatcaExemptionCode);
  addAddressMetadata(metadata, "address", values.address);
  addMetadataEntry(metadata, "tax_id", values.taxId);
  return metadata;
};

const transformW8BEN = (values: TaxFormValues): DocumentValueInput[] => {
  const metadata: DocumentValueInput[] = [];
  addMetadataEntry(metadata, "type", TAX_FORM_TYPE_MAP.W8BEN);
  addMetadataEntry(metadata, "name", values.name);
  addMetadataEntry(metadata, "country_of_citizenship", values.countryOfCitizenship);
  addAddressMetadata(metadata, "permanent_residence_address", values.permanentAddress);
  addMetadataEntry(metadata, "date_of_birth", values.dateOfBirth, "date");
  addMetadataEntry(metadata, "us_tin", values.usTin);
  addMetadataEntry(metadata, "foreign_tin", values.foreignTin);
  addMetadataEntry(
    metadata,
    "ftin_not_legally_required",
    values.ftinNotLegallyRequired,
    "boolean",
  );
  addMetadataEntry(metadata, "treaty_country", values.treatyCountry);
  addMetadataEntry(metadata, "treaty_rate_claimed", values.treatyRateClaimed);
  addMetadataEntry(metadata, "treaty_income_type", values.incomeType);
  return metadata;
};

const transformW8BENE = (values: TaxFormValues): DocumentValueInput[] => {
  const metadata: DocumentValueInput[] = [];
  addMetadataEntry(metadata, "type", TAX_FORM_TYPE_MAP.W8BENE);
  addMetadataEntry(metadata, "name", values.entityName);
  addMetadataEntry(metadata, "country_of_incorporation", values.countryOfIncorporation);
  addMetadataEntry(metadata, "disregarded_entity_name", values.disregardedEntityName);
  addMetadataEntry(metadata, "chapter_3_status", values.chapter3Status);
  addMetadataEntry(metadata, "chapter_4_status", values.chapter4Status);
  addAddressMetadata(metadata, "permanent_residence_address", values.permanentAddress);
  addMetadataEntry(metadata, "us_tin", values.usTin);
  addMetadataEntry(metadata, "foreign_tin", values.foreignTin);
  addMetadataEntry(metadata, "giin", values.giin);
  addMetadataEntry(metadata, "giin_verified", values.giinVerified, "boolean");
  addMetadataEntry(metadata, "sponsor_name", values.sponsorName);
  addMetadataEntry(
    metadata,
    "is_treaty_country_resident",
    values.isResidentOfTreatyCountry,
    "boolean",
  );
  addMetadataEntry(metadata, "treaty_country", values.treatyCountry);
  addMetadataEntry(
    metadata,
    "has_limitation_on_benefits",
    values.hasLimitationOnBenefits,
    "boolean",
  );
  // For LOB: if "other", use lobArticleAndParagraph, else use lobType
  const lobValue =
    values.lobType === LOB_TYPE_OTHER ? values.lobArticleAndParagraph : values.lobType;
  addMetadataEntry(metadata, "limitation_on_benefits_type", lobValue);
  addMetadataEntry(
    metadata,
    "is_qualified_resident_for_dividends",
    values.claimsDividendsBenefits,
    "boolean",
  );
  addMetadataEntry(metadata, "treaty_rate_claimed", values.treatyRateClaimed);
  addMetadataEntry(metadata, "treaty_income_type", values.incomeType);
  return metadata;
};

const transformW8ECI = (values: TaxFormValues): DocumentValueInput[] => {
  const metadata: DocumentValueInput[] = [];
  addMetadataEntry(metadata, "type", TAX_FORM_TYPE_MAP.W8ECI);
  addMetadataEntry(metadata, "entity_name", values.entityName);
  addMetadataEntry(metadata, "country_of_incorporation", values.countryOfIncorporation);
  addMetadataEntry(metadata, "entity_type", values.entityType);
  addAddressMetadata(metadata, "foreign_address", values.foreignAddress);
  addAddressMetadata(metadata, "us_business_address", values.usBusinessAddress);
  addMetadataEntry(metadata, "us_tin_type", values.usTinType);
  addMetadataEntry(metadata, "us_tin", values.usTin);
  addMetadataEntry(metadata, "income_description", values.incomeDescription);
  addMetadataEntry(metadata, "dealer_ptp_checkbox", values.certifyAsDealerPtp, "boolean");
  return metadata;
};

const transformW8EXP = (values: TaxFormValues): DocumentValueInput[] => {
  const metadata: DocumentValueInput[] = [];
  addMetadataEntry(metadata, "type", TAX_FORM_TYPE_MAP.W8EXP);
  addMetadataEntry(metadata, "entity_name", values.entityName);
  addMetadataEntry(metadata, "country_of_incorporation", values.countryOfIncorporation);
  addMetadataEntry(metadata, "org_type", values.orgType);
  addMetadataEntry(metadata, "chapter_4_status", values.chapter4Status);
  addAddressMetadata(metadata, "permanent_residence_address", values.permanentAddress);
  addMetadataEntry(metadata, "us_tin", values.usTin);
  addMetadataEntry(metadata, "giin", values.giin);
  addMetadataEntry(metadata, "giin_verified", values.giinVerified, "boolean");
  addMetadataEntry(metadata, "foreign_tin", values.foreignTin);
  return metadata;
};

const transformW8IMY = (values: TaxFormValues): DocumentValueInput[] => {
  const metadata: DocumentValueInput[] = [];
  addMetadataEntry(metadata, "type", TAX_FORM_TYPE_MAP.W8IMY);
  addMetadataEntry(metadata, "entity_name", values.entityName);
  addMetadataEntry(metadata, "country_of_incorporation", values.countryOfIncorporation);
  addMetadataEntry(metadata, "disregarded_entity_name", values.disregardedEntityName);
  addMetadataEntry(metadata, "chapter_3_status", values.chapter3Status);
  addMetadataEntry(metadata, "chapter_4_status", values.chapter4Status);
  addAddressMetadata(metadata, "permanent_residence_address", values.permanentAddress);
  addMetadataEntry(metadata, "us_tin", values.usTin);
  addMetadataEntry(metadata, "us_tin_type", values.usTinType);
  addMetadataEntry(metadata, "giin", values.giin);
  addMetadataEntry(metadata, "giin_verified", values.giinVerified, "boolean");
  addMetadataEntry(metadata, "foreign_tin", values.foreignTin);
  addMetadataEntry(metadata, "sponsor_name", values.sponsorName);
  return metadata;
};

const transformCrsIndividual = (values: TaxFormValues): DocumentValueInput[] => {
  const metadata: DocumentValueInput[] = [];
  addMetadataEntry(metadata, "type", TAX_FORM_TYPE_MAP.CRS_INDIVIDUAL);
  addMetadataEntry(metadata, "name", values.name);
  addMetadataEntry(metadata, "date_of_birth", values.dateOfBirth, "date");
  addMetadataEntry(metadata, "city_of_birth", values.cityOfBirth);
  addMetadataEntry(metadata, "country_of_birth", values.countryOfBirth);
  addAddressMetadata(metadata, "permanent_residence_address", values.permanentAddress);
  addMetadataEntry(metadata, "us_status_declaration", values.usStatusDeclaration);
  addMetadataEntry(metadata, "us_tin", values.usTin);
  addTaxResidenciesMetadata(metadata, "tax_residencies", values.taxResidencies);
  return metadata;
};

const transformCrsEntity = (values: TaxFormValues): DocumentValueInput[] => {
  const metadata: DocumentValueInput[] = [];
  addMetadataEntry(metadata, "type", TAX_FORM_TYPE_MAP.CRS_ENTITY);
  addMetadataEntry(metadata, "entity_name", values.entityName);
  addMetadataEntry(metadata, "country_of_incorporation", values.countryOfIncorporation);
  addAddressMetadata(metadata, "permanent_residence_address", values.permanentAddress);
  addMetadataEntry(metadata, "fatca_status", values.fatcaStatus);
  addMetadataEntry(metadata, "crs_status", values.crsStatus);
  addMetadataEntry(metadata, "giin", values.giin);
  addMetadataEntry(metadata, "sponsor_name", values.sponsorName);
  addMetadataEntry(metadata, "sponsor_giin", values.sponsorGiin);
  addMetadataEntry(metadata, "giin_verified", values.giinVerified, "boolean");
  addTaxResidenciesMetadata(metadata, "tax_residencies", values.taxResidencies);
  return metadata;
};

const transformWithholdingStatement = (): DocumentValueInput[] => [
  { key: "type", value: TAX_FORM_TYPE_MAP.WITHHOLDING_STATEMENT, type: "string" },
];

// ─── Reverse: metadata → TaxFormValues ───────────────────────────────────────

const getVal = (m: DocumentValueType[] | null, key: string): string | undefined =>
  m?.find((e) => e?.key === key)?.value;

const parseAddress = (raw: string | undefined): AddressValues => {
  if (!raw)
    return {
      country: "",
      streetLine1: "",
      streetLine2: "",
      city: "",
      stateOrProvince: "",
      zipCode: "",
    };
  try {
    const p = JSON.parse(raw) as BackendAddressData;
    return {
      country: p.country ?? "",
      streetLine1: p.street ?? "",
      streetLine2: p.street2 ?? "",
      city: p.city ?? "",
      stateOrProvince: p.state ?? "",
      zipCode: p.postal_code ?? "",
    };
  } catch {
    return {
      country: "",
      streetLine1: "",
      streetLine2: "",
      city: "",
      stateOrProvince: "",
      zipCode: "",
    };
  }
};

const parseBool = (raw: string | undefined): boolean => raw === "true";

const parseDate = (raw: string | undefined): Date | null => {
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseTaxResidencies = (raw: string | undefined): TaxResidencyEntry[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as BackendTaxResidencyData[];
    return parsed.map((e, i) => ({
      id: String(i),
      jurisdiction: e.jurisdiction ?? "",
      hasTaxReferenceNumber: e.has_tax_reference_number ?? false,
      taxReferenceNumberType: e.tax_reference_number_type ?? "",
      taxReferenceNumber: e.tax_reference_number ?? "",
      reasonForNoTin: e.reason_for_no_tin ?? "",
    }));
  } catch {
    return [];
  }
};

const KNOWN_LOB_VALUES = new Set([
  "government",
  "tax_exempt_pension",
  "other_tax_exempt",
  "publicly_traded_corp",
  "subsidiary_publicly_traded",
  "ownership_base_erosion",
  "derivative_benefits",
  "active_trade_business",
  "favorable_discretionary",
  "no_lob_article",
]);

const parseLobType = (raw: string | undefined) => {
  if (!raw) return { lobType: "", lobArticleAndParagraph: "" };
  if (KNOWN_LOB_VALUES.has(raw)) return { lobType: raw, lobArticleAndParagraph: "" };
  return { lobType: "other", lobArticleAndParagraph: raw };
};

const parseW9 = (m: DocumentValueType[] | null): TaxFormValues => ({
  name: getVal(m, "name") ?? "",
  disregardedEntityName: getVal(m, "disregarded_entity_name") ?? "",
  taxClassification: getVal(m, "tax_classification") ?? "",
  llcTaxClassification: getVal(m, "llc_classification") ?? "",
  exemptPayeeCode: getVal(m, "exempt_payee_code") ?? "",
  fatcaExemptionCode: getVal(m, "exemption_from_fatca_reporting_code") ?? "",
  address: parseAddress(getVal(m, "address")),
  taxId: getVal(m, "tax_id") ?? "",
});

const parseW8BEN = (m: DocumentValueType[] | null): TaxFormValues => ({
  name: getVal(m, "name") ?? "",
  countryOfCitizenship: getVal(m, "country_of_citizenship") ?? "",
  permanentAddress: parseAddress(getVal(m, "permanent_residence_address")),
  dateOfBirth: parseDate(getVal(m, "date_of_birth")),
  usTin: getVal(m, "us_tin") ?? "",
  foreignTin: getVal(m, "foreign_tin") ?? "",
  ftinNotLegallyRequired: parseBool(getVal(m, "ftin_not_legally_required")),
  treatyCountry: getVal(m, "treaty_country") ?? "",
  treatyRateClaimed: getVal(m, "treaty_rate_claimed") ?? "",
  incomeType: getVal(m, "treaty_income_type") ?? "",
});

const parseW8BENE = (m: DocumentValueType[] | null): TaxFormValues => {
  const lob = parseLobType(getVal(m, "limitation_on_benefits_type"));
  const chapter4Status = getVal(m, "chapter_4_status") ?? "";
  const fatcaPartConfirmations = FATCA_PARTS.reduce<Record<string, boolean>>(
    (acc, part) => ({
      ...acc,
      [`chapter4Part${part.partNumber}Confirmed`]: chapter4Status === part.statusValue,
    }),
    {},
  );
  return {
    entityName: getVal(m, "name") ?? "",
    countryOfIncorporation: getVal(m, "country_of_incorporation") ?? "",
    disregardedEntityName: getVal(m, "disregarded_entity_name") ?? "",
    chapter3Status: getVal(m, "chapter_3_status") ?? "",
    chapter4Status,
    permanentAddress: parseAddress(getVal(m, "permanent_residence_address")),
    usTin: getVal(m, "us_tin") ?? "",
    foreignTin: getVal(m, "foreign_tin") ?? "",
    giin: getVal(m, "giin") ?? "",
    giinVerified: parseBool(getVal(m, "giin_verified")),
    sponsorName: getVal(m, "sponsor_name") ?? "",
    isResidentOfTreatyCountry: parseBool(getVal(m, "is_treaty_country_resident")),
    treatyCountry: getVal(m, "treaty_country") ?? "",
    hasLimitationOnBenefits: parseBool(getVal(m, "has_limitation_on_benefits")),
    lobType: lob.lobType,
    lobArticleAndParagraph: lob.lobArticleAndParagraph,
    claimsDividendsBenefits: parseBool(getVal(m, "is_qualified_resident_for_dividends")),
    treatyRateClaimed: getVal(m, "treaty_rate_claimed") ?? "",
    incomeType: getVal(m, "treaty_income_type") ?? "",
    ...fatcaPartConfirmations,
  };
};

const parseW8ECI = (m: DocumentValueType[] | null): TaxFormValues => ({
  entityName: getVal(m, "entity_name") ?? "",
  countryOfIncorporation: getVal(m, "country_of_incorporation") ?? "",
  entityType: getVal(m, "entity_type") ?? "",
  foreignAddress: parseAddress(getVal(m, "foreign_address")),
  usBusinessAddress: parseAddress(getVal(m, "us_business_address")),
  usTinType: getVal(m, "us_tin_type") ?? "",
  usTin: getVal(m, "us_tin") ?? "",
  incomeDescription: getVal(m, "income_description") ?? "",
  certifyAsDealerPtp: parseBool(getVal(m, "dealer_ptp_checkbox")),
});

const parseW8EXP = (m: DocumentValueType[] | null): TaxFormValues => ({
  entityName: getVal(m, "entity_name") ?? "",
  countryOfIncorporation: getVal(m, "country_of_incorporation") ?? "",
  orgType: getVal(m, "org_type") ?? "",
  chapter4Status: getVal(m, "chapter_4_status") ?? "",
  permanentAddress: parseAddress(getVal(m, "permanent_residence_address")),
  usTin: getVal(m, "us_tin") ?? "",
  giin: getVal(m, "giin") ?? "",
  giinVerified: parseBool(getVal(m, "giin_verified")),
  foreignTin: getVal(m, "foreign_tin") ?? "",
});

const parseW8IMY = (m: DocumentValueType[] | null): TaxFormValues => ({
  entityName: getVal(m, "entity_name") ?? "",
  countryOfIncorporation: getVal(m, "country_of_incorporation") ?? "",
  disregardedEntityName: getVal(m, "disregarded_entity_name") ?? "",
  chapter3Status: getVal(m, "chapter_3_status") ?? "",
  chapter4Status: getVal(m, "chapter_4_status") ?? "",
  permanentAddress: parseAddress(getVal(m, "permanent_residence_address")),
  usTin: getVal(m, "us_tin") ?? "",
  usTinType: getVal(m, "us_tin_type") ?? "",
  giin: getVal(m, "giin") ?? "",
  giinVerified: parseBool(getVal(m, "giin_verified")),
  foreignTin: getVal(m, "foreign_tin") ?? "",
  sponsorName: getVal(m, "sponsor_name") ?? "",
});

const parseCrsIndividual = (m: DocumentValueType[] | null): TaxFormValues => ({
  name: getVal(m, "name") ?? "",
  dateOfBirth: parseDate(getVal(m, "date_of_birth")),
  cityOfBirth: getVal(m, "city_of_birth") ?? "",
  countryOfBirth: getVal(m, "country_of_birth") ?? "",
  permanentAddress: parseAddress(getVal(m, "permanent_residence_address")),
  usStatusDeclaration: getVal(m, "us_status_declaration") ?? "",
  usTin: getVal(m, "us_tin") ?? "",
  taxResidencies: parseTaxResidencies(getVal(m, "tax_residencies")),
});

const parseCrsEntity = (m: DocumentValueType[] | null): TaxFormValues => ({
  entityName: getVal(m, "entity_name") ?? "",
  countryOfIncorporation: getVal(m, "country_of_incorporation") ?? "",
  permanentAddress: parseAddress(getVal(m, "permanent_residence_address")),
  fatcaStatus: getVal(m, "fatca_status") ?? "",
  crsStatus: getVal(m, "crs_status") ?? "",
  giin: getVal(m, "giin") ?? "",
  sponsorName: getVal(m, "sponsor_name") ?? "",
  sponsorGiin: getVal(m, "sponsor_giin") ?? "",
  giinVerified: parseBool(getVal(m, "giin_verified")),
  taxResidencies: parseTaxResidencies(getVal(m, "tax_residencies")),
});

export const transformMetadataToTaxFormValues = (
  formType: TaxFormType,
  metadata: DocumentValueType[] | null,
): TaxFormValues => {
  switch (formType) {
    case "W9":
      return parseW9(metadata);
    case "W8BEN":
      return parseW8BEN(metadata);
    case "W8BENE":
      return parseW8BENE(metadata);
    case "W8ECI":
      return parseW8ECI(metadata);
    case "W8EXP":
      return parseW8EXP(metadata);
    case "W8IMY":
      return parseW8IMY(metadata);
    case "CRS_INDIVIDUAL":
      return parseCrsIndividual(metadata);
    case "CRS_ENTITY":
      return parseCrsEntity(metadata);
    case "WITHHOLDING_STATEMENT":
      return {};
    default: {
      const _exhaustiveCheck: never = formType;
      return _exhaustiveCheck;
    }
  }
};

export const transformTaxFormValuesToMetadata = (
  formType: TaxFormType,
  values: TaxFormValues,
): DocumentValueInput[] => {
  switch (formType) {
    case "W9":
      return transformW9(values);
    case "W8BEN":
      return transformW8BEN(values);
    case "W8BENE":
      return transformW8BENE(values);
    case "W8ECI":
      return transformW8ECI(values);
    case "W8EXP":
      return transformW8EXP(values);
    case "W8IMY":
      return transformW8IMY(values);
    case "CRS_INDIVIDUAL":
      return transformCrsIndividual(values);
    case "CRS_ENTITY":
      return transformCrsEntity(values);
    case "WITHHOLDING_STATEMENT":
      return transformWithholdingStatement();
    default: {
      const _exhaustiveCheck: never = formType;
      return _exhaustiveCheck;
    }
  }
};
